/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Question } from "../../../models/interfaces/questions/question";
import { useTranslation } from "react-i18next";
import BackPainAreasTab from "../../pain-areas/back-pain-areas-tab";
import FrontPainAreasTab from "../../pain-areas/front-pain-area-tab";
import { Option } from "../../../models/interfaces/option";
import { PainAreaFacingKey, PainAreaMap } from "../../pain-areas/pain-area-map";
import { FacingDirection } from "../../../models/enumerations/facing-directions";
import AppContentTypeToggle from "../../app-content-type-toggle/app-content-type-toggle";
import { createLogger } from "../../../utilities/logger";

const log = createLogger("MultipleChoicePainAreas");

const COMPONENT_CLASS = "c-multiple-choice-pain-areas";

export interface MultipleChoicePainAreasProps {
    initialAnswer?: string;
    handleAnswerChange: (selected: string, question: Question) => void;
    question: Question;
}

const MultipleChoicePainAreas: React.FC<MultipleChoicePainAreasProps> = (
    props
) => {
    const [options, setOptions] = useState<Option[]>([]);
    const { t } = useTranslation();
    const [toggle, setToggle] = useState<'left' | 'right'>('left');
    useEffect(() => {
        setOptions(getOptions());
    }, []);

    const getOptions = (): Option[] => {
        let preSelected: Array<{ value: string; facingDirection: string }> = [];

        try {
            if (props.initialAnswer) {
                const parsed = JSON.parse(props.initialAnswer);
                if (Array.isArray(parsed)) {
                    preSelected = parsed.flatMap((item) => {
                        if (item && typeof item === "object") {
                            const [value, facing] = Object.entries(item)[0] ?? [];
                            if (typeof value === "string" && typeof facing === "string") {
                                return [{ value, facingDirection: facing }];
                            }
                        }
                        return [];
                    });
                }
            }
        } catch (err) {
            log.warn("Invalid initialAnswer JSON", {
                exception: err,
                initialAnswer: props.initialAnswer,
                questionId: props.question?.questionId,
            });
        }

        return (
            props.question?.answerOptions?.map((answerOption) => {
                const match = preSelected.find(
                    (sel) =>
                        sel.value === answerOption.answerId &&
                        sel.facingDirection === answerOption.answerFacingDirection
                );

                const key = `${answerOption.answerId}_${answerOption.answerFacingDirection}` as PainAreaFacingKey;
                const meta = PainAreaMap[key];
                return {
                    value: answerOption.answerId,
                    label: t(meta?.label),
                    facingDirection: answerOption.answerFacingDirection,
                    isSelected: !!match,
                    top: meta?.top,
                    left: meta?.left,
                    size: meta?.size,
                    labelSide: meta?.labelSide ?? "right",
                } as Option;
            }) ?? []
        );
    };


    const handleChange = (option: Option, checked: boolean): void => {
        options.filter((x) => x.value === option.value && x.facingDirection === option.facingDirection)[0].isSelected =
            checked;

        setOptions(options);
        props.handleAnswerChange(getAnswer(), props.question);
    };

    const getAnswer = (): string => {
        const selected = options.filter((x) => x.isSelected);

        const mapped = selected.map((opt) => ({
            [opt.value]: opt.facingDirection,
        }));
        return JSON.stringify(mapped);
    };

    const frontOptions = options.filter(
        (o) => o.facingDirection === FacingDirection.FRONT
    );
    const backOptions = options.filter(
        (o) => o.facingDirection === FacingDirection.BACK
    );

    return (
        <div
            className={`${COMPONENT_CLASS}`}
            data-testid="multiple-choice-pain-areas-wrapper"
        >
            <AppContentTypeToggle
                isOption1={toggle === 'left'}
                isOption2={toggle === 'right'}
                onClickOption1={() => { setToggle('left') }}
                onClickOption2={() => { setToggle('right') }}
                textOption1={t('app.assessments.tabs.front')}
                textOption2={t('app.assessments.tabs.back')}
                width={350}
            />
            {
                toggle === 'left' ?
                    <FrontPainAreasTab options={frontOptions} onChange={handleChange} />
                    :
                    <BackPainAreasTab options={backOptions} onChange={handleChange} />
            }
        </div>
    );
};

export default MultipleChoicePainAreas;
